import { FiDownload, FiFile } from "react-icons/fi";
import { cn } from "utils/helpers";
import { formatDate } from "../../../utils/formatDate";
import type { RevisionFile } from "../../../types/scoring.type";

interface RevisionFileListProps {
	files: RevisionFile[];
}

const getFileTypeBadgeStyle = (fileType: string | null): string => {
	if (!fileType) {
		return "bg-gray-100 text-gray-600";
	}
	const lower = fileType.toLowerCase();
	if (lower === "pdf") {
		return "bg-red-100 text-red-700";
	}
	if (lower === "doc" || lower === "docx") {
		return "bg-blue-100 text-blue-700";
	}
	if (lower === "xls" || lower === "xlsx" || lower === "csv") {
		return "bg-green-100 text-green-700";
	}
	return "bg-gray-100 text-gray-600";
};

export const RevisionFileList = ({ files }: RevisionFileListProps) => {
	return (
		<div className="space-y-2" data-testid="revision-file-list">
			{files.map((file) => (
				<div
					key={file.id}
					className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 hover:bg-gray-50 transition-colors"
					data-testid={`revision-file-${file.id}`}
				>
					<div className="flex items-center gap-3 min-w-0">
						<FiFile size={16} className="shrink-0 text-gray-400" aria-hidden="true" />
						<div className="min-w-0">
							<p className="text-sm font-medium text-gray-700 truncate">
								{file.fileName}
							</p>
							<div className="flex items-center gap-2 mt-0.5">
								<span
									className={cn(
										"inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase",
										getFileTypeBadgeStyle(file.fileType)
									)}
								>
									{file.fileType || "file"}
								</span>
								<span className="text-xs text-gray-400">
									{formatDate(file.uploadedAt)}
								</span>
								{file.uploadedBy && (
									<span className="text-xs text-gray-400">
										by {file.uploadedBy}
									</span>
								)}
							</div>
						</div>
					</div>
					{file.downloadUrl ? (
						<a
							href={file.downloadUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="shrink-0 ml-2 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
							data-testid={`download-file-${file.id}`}
						>
							<FiDownload size={14} aria-hidden="true" />
							Download
						</a>
					) : (
						<span className="shrink-0 ml-2 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-gray-400">
							<FiFile size={14} aria-hidden="true" />
							No file
						</span>
					)}
				</div>
			))}
		</div>
	);
};
